// @ts-strict-ignore
import {nanoid} from 'nanoid';

import {CustomProperty} from 'common/types/reports/query';
import {getOwnAttr} from 'common/util';
import {Property} from 'common/query-builder/query-block/properties-menu/types';
import {ResourceType} from 'common/report/constants';

import {QUERY_BUILDER_RESOURCE_TYPE_MAPPING} from '../property-filter-menu/models/insights-filter';

const INLINE_CUSTOM_PROPERTY_ID_PREFIX = `$temp-`;

export function isPersistedCustomProperty(customPropertyId) {
  return customPropertyId && !customPropertyId.toString().startsWith(INLINE_CUSTOM_PROPERTY_ID_PREFIX);
}

export function isInlineCustomProperty(customPropertyId) {
  return customPropertyId && customPropertyId.toString().startsWith(INLINE_CUSTOM_PROPERTY_ID_PREFIX);
}

export function isBehavioralCustomProperty(property: CustomProperty) {
  return !!getOwnAttr(property, `behavior`);
}

export function getInlineCustomPropertyId(customPropertyId) {
  return customPropertyId || `${INLINE_CUSTOM_PROPERTY_ID_PREFIX}${nanoid()}`;
}

export function getBorrowedProperty(customProperty?: CustomProperty): Property | null {
  if (!customProperty) {
    return null;
  }
  return Object.values(customProperty.composedProperties || {}).find((prop: Property) => `borrowEvent` in prop);
}

/**
 * Checks if a custom property is itself a borrowed property or references a nested custom property that is a
 * borrowed property
 */
export function isBorrowedPropertyReferenced(customProperty: CustomProperty): boolean {
  if (!customProperty.customPropertyId) {
    // // inline custom property
    return Object.values(customProperty.composedProperties).some((prop: Property) => {
      // check if inline custom property references a saved custom property that references a borrowed prop
      return Boolean(prop.customProperty?.referencesBorrowedProperty);
    });
  }
  return customProperty.referencesBorrowedProperty;
}

export function trackingDataForBorrowedProperty(customProperty?: CustomProperty) {
  const borrowedProperty = getBorrowedProperty(customProperty);
  if (!borrowedProperty) {
    return null;
  }

  return {
    name: customProperty.name,
    customPropertyId: customProperty.customPropertyId,
    borrowEvent: borrowedProperty.borrowEvent.value,
    borrowProperty: borrowedProperty.value,
  };
}

/**
 * Returns whether a custom prop has a group property. Duplicated from iron/common/query-builder/query-block/properties-menu/index.tsx
 * @param {CustomProperty} customProperty
 * @returns {boolean}
 */
export function customPropHasGroupProperty(customProperty: CustomProperty): boolean {
  if (!(`composedProperties` in customProperty)) {
    return false;
  }
  for (const composedProperty of Object.values(customProperty.composedProperties)) {
    if (isGroupProperty(composedProperty)) {
      return true;
    }
  }

  const nestedCustomProperties = Object.values(customProperty.composedProperties).filter(
    (composedProperty: any) => !!composedProperty.customPropertyId,
  );
  return nestedCustomProperties.some((nestedCustomProperty) => customPropHasGroupProperty(nestedCustomProperty));
}

/** Copied from iron/common/widgets/property-filter-menu/models/utils.ts */
// TODO: After CustomProperty.ComposedProperty type gets stricter, update this type too
function isGroupProperty(property: TODO) {
  return !!property.dataGroupId && !Array.isArray(property.propertyName);
}

export const PROPERTY_TOKEN_PREFIX = `@"""`;
export const PROPERTY_TOKEN_SUFFIX = `"""@`;

export function getTokenTextForEncodedProperty(encodedProperty: string): string {
  return `${PROPERTY_TOKEN_PREFIX}${encodedProperty}${PROPERTY_TOKEN_SUFFIX}`;
}

export const PERSISTENT_EMPTY_PROPERTY_TOKEN = getTokenTextForEncodedProperty(`{ }`);
export const CUSTOM_PROPERTY_RESOURCE_TYPES = {
  [ResourceType.Event]: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.Event],
  [ResourceType.User]: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.User],
};
